import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { deleteBranch } from '../../../api'
import { toast } from 'react-toastify'
import { useAuth } from '../core/Auth'

export function ConfirmDeleteBranch() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const deleteBranchCode = searchParams.get('deleteBranchCode') || ''
  const [loading, setLoading] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { logout } = useAuth()

  useEffect(() => {
    if (!deleteBranchCode) {
      setErrorMessage('Invalid link. Please request a new deletion from Branch Settings.'.toTranslatedDisplay())
    }
  }, [deleteBranchCode])

  const handleDelete = async () => {
    if (!deleteBranchCode) return
    setLoading(true)
    setErrorMessage('')
    try {
      await deleteBranch({ deleteBranchCode })
      setFormSuccess(true)
      toast.success('Branch deleted successfully.'.toTranslatedDisplay())
      logout()
      setTimeout(() => navigate('/auth/login'), 3000)
    } catch (error: any) {
      const msg: string = error?.message || ''
      // deleteWithCacheUpdate wraps HTTP errors; detect by message content
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('gone')) {
        setErrorMessage(
          'This link has expired. Please go to Branch Settings and request a new deletion.'.toTranslatedDisplay()
        )
      } else if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('invalid')) {
        setErrorMessage(
          'This link is no longer valid. The branch may have already been deleted.'.toTranslatedDisplay()
        )
      } else {
        setErrorMessage(msg || 'Something went wrong. Please try again or contact support.'.toTranslatedDisplay())
      }
    } finally {
      setLoading(false)
    }
  }

  if (!deleteBranchCode && !formSuccess) {
    return (
      <div className='text-center'>
        <div className='mb-10 bg-light-danger p-8 rounded'>
          <i className='bi bi-exclamation-triangle fs-2x text-danger mb-3 d-block'></i>
          <div className='text-danger fw-bold'>
            {'Invalid link. Please request a new deletion from Branch Settings.'.toTranslatedDisplay()}
          </div>
        </div>
        <Link to='/settings' className='btn btn-light'>
          {'Go to Settings'.toTranslatedDisplay()}
        </Link>
      </div>
    )
  }

  if (formSuccess) {
    return (
      <div className='text-center'>
        <div className='mb-10 bg-light-success p-8 rounded'>
          <i className='bi bi-check-circle fs-2x text-success mb-3 d-block'></i>
          <div className='text-success fw-bold fs-4 mb-2'>{'Branch Deleted'.toTranslatedDisplay()}</div>
          <div className='text-success'>{'The branch has been permanently deleted.'.toTranslatedDisplay()}</div>
          <div className='text-muted mt-2 fs-7'>{'Redirecting to login...'.toTranslatedDisplay()}</div>
        </div>
        <Link to='/auth/login' className='btn btn-primary'>
          {'Go to Login'.toTranslatedDisplay()}
        </Link>
      </div>
    )
  }

  return (
    <div className='text-center'>
      <h1 className='text-gray-900 fw-bolder mb-3'>{'Confirm Branch Deletion'.toTranslatedDisplay()}</h1>

      <div className='mb-8 bg-light-danger p-8 rounded text-start'>
        <div className='text-danger fw-bold mb-3'>
          <i className='bi bi-exclamation-triangle fs-2x text-danger mb-3 d-block text-center'></i>
          <div className='text-center fs-4 mb-4'>{'This action CANNOT be undone.'.toTranslatedDisplay()}</div>
        </div>
        <p className='text-gray-700 mb-3'>
          {'Clicking the button below will permanently delete this branch and all its data:'.toTranslatedDisplay()}
        </p>
        <ul className='text-danger mb-0'>
          <li>{'All transactions and invoices for this branch'.toTranslatedDisplay()}</li>
          <li>{'All ledger and accounting entries'.toTranslatedDisplay()}</li>
          <li>{'All inventory and stock records'.toTranslatedDisplay()}</li>
          <li>{'All number sequences for this branch'.toTranslatedDisplay()}</li>
        </ul>
        <p className='text-gray-600 mt-3 mb-0'>
          {'The main business and other branches will not be affected.'.toTranslatedDisplay()}
        </p>
      </div>

      {errorMessage && (
        <div className='mb-6 alert alert-danger'>
          <div className='alert-text'>{errorMessage}</div>
          {errorMessage.includes('expired') && (
            <Link to='/settings' className='btn btn-sm btn-outline-danger mt-2'>
              {'Go to Branch Settings'.toTranslatedDisplay()}
            </Link>
          )}
        </div>
      )}

      <div className='d-flex flex-wrap justify-content-center gap-3'>
        <button
          type='button'
          className='btn btn-danger'
          onClick={handleDelete}
          disabled={loading}
        >
          {loading ? (
            <span className='indicator-progress'>
              {'Deleting...'.toTranslatedDisplay()}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          ) : (
            <>
              <i className='bi bi-trash me-2'></i>
              {'Yes, Delete Branch Permanently'.toTranslatedDisplay()}
            </>
          )}
        </button>
        <Link to='/dashboard'>
          <button type='button' className='btn btn-light' disabled={loading}>
            {'Cancel — Go Back'.toTranslatedDisplay()}
          </button>
        </Link>
      </div>
    </div>
  )
}
